import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IWorkingHoursService } from "services/WorkingHoursService";
import { OverrideWorkingHoursModel } from "services/api/cplapi/CPLAPI";
import { RootState } from "setup";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { container } from "setup/dependency/inversify.config";
import { toast } from "react-toastify";
import dayjs from "global/dayjs";

const initialState = {
  OverrideWorkingHours: new Array<OverrideWorkingHoursModel>(),
  IsLoading: false,
};

export const overrideWorkingHoursSlice = createSlice({
  name: "OverrideWorkingHours",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getOverrideWorkingHours.pending, (state) => {
        state.IsLoading = true;
      })
      .addCase(getOverrideWorkingHours.fulfilled, (state, action) => {
        state.OverrideWorkingHours = action.payload;
        state.IsLoading = false;
      })
      .addCase(getOverrideWorkingHours.rejected, (state) => {
        state.IsLoading = false;
      })
      .addCase(updateWorkingHours.fulfilled, () => {
        toast.success("Override Working Hours Updated");
      })
      .addCase(deleteWorkingHours.fulfilled, () => {
        toast.success("Override Working Hours Deleted");
      });
  },
});

export const getOverrideWorkingHours = createAsyncThunk("OverrideWorkingHours/GetOverrideWorkingHours", async () => {
  const workingHoursService = container.get<IWorkingHoursService>(SERVICE_TYPES.WorkingHoursService);
  return await workingHoursService.getOverrideWorkingHours();
});

export const createWorkingHours = createAsyncThunk(
  "OverrideWorkingHours/CreateOverrideWorkingHours",
  async (overrideWorkingHours: OverrideWorkingHoursModel, thunkAPI) => {
    const workingHoursService = container.get<IWorkingHoursService>(SERVICE_TYPES.WorkingHoursService);
    overrideWorkingHours.openTime = dayjs(overrideWorkingHours.openTime, "HH:mm").format("HH:mm:ss");
    overrideWorkingHours.closeTime = dayjs(overrideWorkingHours.closeTime, "HH:mm").format("HH:mm:ss");
    await workingHoursService.createOverrideWorkingHours(overrideWorkingHours);
    thunkAPI.dispatch(getOverrideWorkingHours());
  }
);

export const updateWorkingHours = createAsyncThunk(
  "OverrideWorkingHours/UpdateOverrideWorkingHours",
  async (overrideWorkingHours: OverrideWorkingHoursModel, thunkAPI) => {
    const workingHoursService = container.get<IWorkingHoursService>(SERVICE_TYPES.WorkingHoursService);
    overrideWorkingHours.openTime = dayjs(overrideWorkingHours.openTime, "HH:mm").format("HH:mm:ss");
    overrideWorkingHours.closeTime = dayjs(overrideWorkingHours.closeTime, "HH:mm").format("HH:mm:ss");
    await workingHoursService.updateOverrideWorkingHours(overrideWorkingHours);
    thunkAPI.dispatch(getOverrideWorkingHours());
  }
);

export const deleteWorkingHours = createAsyncThunk(
  "OverrideWorkingHours/DeleteOverrideWorkingHours",
  async (overrideWorkingHourID: number, thunkAPI) => {
    const workingHoursService = container.get<IWorkingHoursService>(SERVICE_TYPES.WorkingHoursService);
    await workingHoursService.deleteOverrideWorkingHours(overrideWorkingHourID);
    thunkAPI.dispatch(getOverrideWorkingHours());
  }
);

export const selectIsLoading = (state: RootState) => {
  return state.overrideWorkingHours.IsLoading;
};
export const selectOverrideWorkingHours = (state: RootState) => {
  return state.overrideWorkingHours.OverrideWorkingHours;
};

export default overrideWorkingHoursSlice.reducer;
